import type { LocalDate } from '../dates';
import { inRange, spendAmount, type DateRange, type LedgerEntryLike } from '../ledger/counting';
import type { Mutation } from '../mutations';
import type { WeekStart } from '../spending/range';
import type { BuildCtx } from '../spending/types';
import { periodRange, pickCutoffSource, type FundPeriod } from './periods';
import { NO_HOLIDAYS, type IncomeSourceLike } from './schedule';
import { budgetMutation, type BudgetResult } from './types';

export interface FundLike {
  id: string;
  name: string;
  period: FundPeriod;
  /** How much the fund holds each period, in minor units. */
  amountMinor: number;
  startDate: LocalDate;
  /** Per-cutoff funds refill on this source's paydays (null = the first salary source). */
  refillFromIncomeSourceId: string | null;
  position: number;
  /** v0.5: money accounts the fund's money is kept in; the first is the default "From". */
  accountIds: string[];
  deletedAt: string | null;
}

/** A ledger row as funds read it: LedgerEntryLike plus the fund it deducts from. */
export type FundEntryLike = LedgerEntryLike & { fundId: string | null };

export interface FundStatus {
  fund: FundLike;
  range: DateRange;
  spentMinor: number;
  leftMinor: number;
}

export const MAX_FUND_NAME = 40;
export const MAX_FUND_MINOR = 1_000_000_000;
export const FUND_PERIODS: readonly FundPeriod[] = ['weekly', 'per_cutoff', 'monthly'];

/**
 * Net spending (expenses minus refunds) tagged with the fund in the range. Derived, never stored,
 * so moving an expense to another fund changes both balances (Ivan, 2026-11-02).
 */
export function fundSpent(
  entries: readonly FundEntryLike[],
  fundId: string,
  range: DateRange,
): number {
  return entries.reduce(
    (sum, e) => (e.fundId === fundId && inRange(e.date, range) ? sum + spendAmount(e) : sum),
    0,
  );
}

/** The source whose paydays refill a per-cutoff fund. */
export function refillSourceFor(
  fund: Pick<FundLike, 'refillFromIncomeSourceId'>,
  sources: readonly IncomeSourceLike[],
): IncomeSourceLike {
  const own = fund.refillFromIncomeSourceId
    ? sources.find((s) => s.id === fund.refillFromIncomeSourceId && s.deletedAt === null)
    : undefined;
  return own ?? pickCutoffSource(sources);
}

/**
 * What's left in a fund for the period containing `date`. A fund is an envelope per period: it
 * starts fresh when its next period starts, with no carry-over (Ivan, 2026-11-02).
 */
export function fundStatus(
  fund: FundLike,
  entries: readonly FundEntryLike[],
  date: LocalDate,
  opts: {
    weekStart: WeekStart;
    sources: readonly IncomeSourceLike[];
    holidays?: ReadonlySet<LocalDate>;
  },
): FundStatus {
  const range = periodRange(fund.period, date, {
    weekStart: opts.weekStart,
    cutoffSource: refillSourceFor(fund, opts.sources),
    holidays: opts.holidays ?? NO_HOLIDAYS,
  });
  const spentMinor = fundSpent(entries, fund.id, range);
  return { fund, range, spentMinor, leftMinor: fund.amountMinor - spentMinor };
}

export function sortFunds<T extends Pick<FundLike, 'position' | 'name'>>(funds: readonly T[]): T[] {
  return [...funds].sort((a, b) => a.position - b.position || a.name.localeCompare(b.name));
}

export interface FundInput {
  name: string;
  period: FundPeriod;
  amountMinor: number;
  refillFromIncomeSourceId?: string | null;
  /** v0.5: accounts the money is kept in, default first. Left out = unchanged on edit. */
  accountIds?: string[];
}

export type FundError =
  'name_required' | 'name_too_long' | 'name_taken' | 'bad_amount' | 'bad_period';

function validateFund(
  input: FundInput,
  others: readonly Pick<FundLike, 'name' | 'deletedAt'>[],
): FundError | null {
  const name = input.name.trim();
  if (name === '') return 'name_required';
  if (name.length > MAX_FUND_NAME) return 'name_too_long';
  if (
    others.some((f) => f.deletedAt === null && f.name.trim().toLowerCase() === name.toLowerCase())
  ) {
    return 'name_taken';
  }
  if (
    !Number.isInteger(input.amountMinor) ||
    input.amountMinor <= 0 ||
    input.amountMinor > MAX_FUND_MINOR
  ) {
    return 'bad_amount';
  }
  if (!FUND_PERIODS.includes(input.period)) return 'bad_period';
  return null;
}

function fundValues(input: FundInput): Record<string, unknown> {
  return {
    name: input.name.trim(),
    period: input.period,
    amountMinor: input.amountMinor,
    refillFromIncomeSourceId:
      input.period === 'per_cutoff' ? (input.refillFromIncomeSourceId ?? null) : null,
    ...(input.accountIds ? { accountIds: [...new Set(input.accountIds)] } : {}),
  };
}

export function buildAddFund(
  input: FundInput,
  existing: readonly FundLike[],
  ctx: BuildCtx,
  today: LocalDate,
): BudgetResult<{ fundId: string; mutation: Mutation }, FundError> {
  const error = validateFund(input, existing);
  if (error) return { ok: false, error };
  const fundId = ctx.newId();
  const position = existing.reduce((max, f) => Math.max(max, f.position), 0) + 1;
  const mutation = budgetMutation(ctx, 'funds', 'insert', fundId, {
    accountIds: [],
    ...fundValues(input),
    currency: 'PHP',
    startDate: today,
    position,
  });
  return { ok: true, fundId, mutation };
}

export function buildEditFund(
  id: string,
  input: FundInput,
  existing: readonly FundLike[],
  ctx: BuildCtx,
): BudgetResult<{ mutation: Mutation }, FundError> {
  const error = validateFund(
    input,
    existing.filter((f) => f.id !== id),
  );
  if (error) return { ok: false, error };
  return { ok: true, mutation: budgetMutation(ctx, 'funds', 'update', id, fundValues(input)) };
}

/** Soft delete. Past expenses keep their fundId, so history still shows which fund they came from. */
export function buildDeleteFund(id: string, ctx: BuildCtx): Mutation {
  return budgetMutation(ctx, 'funds', 'delete', id);
}

/** Undo of a delete: a plain insert on the soft-deleted id (conventions §14), never ifAbsent. */
export function buildRestoreFund(id: string, ctx: BuildCtx): Mutation {
  return budgetMutation(ctx, 'funds', 'insert', id, {});
}
